import React, { useState, useEffect } from "react";
import { BsFillCloudUploadFill } from "react-icons/bs";
import UserImage from "../../assets/userImage.png"; 
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { RotatingLines, ThreeDots } from "react-loader-spinner";
import { useAuth } from "../../context/Auth"; 
import { useNavigate, useParams } from "react-router-dom";


const InputField = ({ label, name, type = "text", value, onChange, required = false, error }) => (
    <div className="flex flex-col gap-1">
        <label htmlFor={name} className="block text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            type={type}
            name={name}
            id={name}
            value={value || ''}
            onChange={onChange}
            className={`block w-full px-3 py-2 border ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'} rounded-md shadow-sm focus:border-blue-500 sm:text-sm transition-colors duration-200`}
        />
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
);

const RadioGroup = ({ label, name, options, value, onChange, required = false, error }) => (
    <div className="flex flex-col gap-1">
        <label className="block text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-2">
            {options.map((option) => (
                <div key={option.value} className="flex items-center">
                    <input
                        type="radio"
                        id={`${name}-${option.value}`}
                        name={name}
                        value={option.value}
                        checked={value === option.value}
                        onChange={onChange}
                        className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                    />
                    <label htmlFor={`${name}-${option.value}`} className="ml-2 block text-sm text-gray-900">
                        {option.label}
                    </label>
                </div>
            ))}
        </div>
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
);


const VisitorDetail = () => {
    const { requestWithAuth } = useAuth();
    const navigate = useNavigate();
    const { visitor_id } = useParams(); 

    const [visitor, setVisitor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [file, setFile] = useState(null); 

    useEffect(() => {
        const fetchVisitor = async () => {
            try {
                setLoading(true);
                const response = await requestWithAuth(`/members/${visitor_id}/`, "GET");
                setVisitor(response?.personal_information);
                
            } catch (error) {
                console.error("Error fetching visitor:", error);
                toast.error("Failed to fetch visitor details.");
                navigate("/visitors"); 
            } finally {
                setLoading(false);
            }
        };

        if (visitor_id) {
            fetchVisitor();
        }
    }, [visitor_id]);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === "file") {
            setFile(files[0]);
            setVisitor((prevVisitor) => ({
                ...prevVisitor,
                [name]: files[0], 
            }));
        } else {
            setVisitor((prevVisitor) => ({
                ...prevVisitor,
                [name]: value,
            }));
        }
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: undefined,
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!visitor.first_name) newErrors.first_name = "First Name is required.";
        if (!visitor.last_name) newErrors.last_name = "Last Name is required.";
        if (!visitor.gender) newErrors.gender = "Gender is required.";
        if (!visitor.address) newErrors.address = "Address is required.";
        if (!visitor.phone_number) newErrors.phone_number = "Phone Number is required.";
        if (!visitor.date_of_birth) newErrors.date_of_birth = "Date of Birth is required.";
        if (!visitor.hometown) newErrors.hometown = "Hometown is required.";
        if (!visitor.nationality) newErrors.nationality = "Nationality is required.";
        if (!visitor.residence) newErrors.residence = "Residence is required.";
        if (!visitor.language_spoken) newErrors.language_spoken = "Language Spoken is required.";
        if (!visitor.marital_status) newErrors.marital_status = "Marital Status is required.";
        if (!visitor.born_again) newErrors.born_again = "Please specify if born again.";
        if (!visitor.holy_spirit_baptized) newErrors.holy_spirit_baptized = "Please specify if Holy Spirit baptized.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const submitForm = async () => {
        if (!validateForm()) {
            toast.error("Please fill in all required fields correctly.");
            return;
        }

        setSubmitting(true);
        const formData = new FormData();
        for (const key in visitor) {
            
            if (visitor[key] !== null && visitor[key] !== undefined) {
                formData.append(key, visitor[key]);
            }
        }
        
        if (file) {
            formData.append("profile_picture", file);
        }

        try {
            await requestWithAuth(`/members/${visitor_id}/`, "PUT", formData, {
                'Content-Type': 'multipart/form-data',
            });
            Swal.fire({
                icon: "success",
                title: "Updated!",
                text: "Visitor details have been updated successfully.",
                confirmButtonColor: '#3085d6',
            }).then(() => {
                navigate("/visitors"); 
            });
        } catch (error) {
            console.error("Update error:", error);
            const errorMessage = error.response?.data?.detail || "An error occurred during update. Please try again.";
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const convertToMember = async () => {
        Swal.fire({
            title: 'Are you sure?',
            text: "Do you want to convert this visitor to a permanent member?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, convert!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                setSubmitting(true);
                try {
                    
                    await requestWithAuth(`/members/${visitor_id}/`, "PUT", { is_permanent_member: true, membership_status : "Active" });
                    Swal.fire(
                        'Converted!',
                        'The visitor has been successfully converted to a permanent member.',
                        'success'
                    ).then(() => {
                        navigate("/members"); 
                    });
                } catch (error) {
                    console.error("Conversion error:", error);
                    const errorMessage = error.response?.data?.detail || "An error occurred during conversion. Please try again.";
                    toast.error(errorMessage);
                } finally {
                    setSubmitting(false);
                }
            }
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ThreeDots height="80" width="80" radius="9" color="#2563EB" ariaLabel="loading" visible={true} />
            </div>
        );
    }

    if (!visitor) {
        return (
            <div className="min-h-screen flex items-center justify-center text-red-500 text-lg">
                Visitor not found or an error occurred.
            </div>
        );
    }

    return (
        <div className="min-h-screen p-6">
            <div className="mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Header */}
                <div className="px-6 py-5 bg-blue-600 text-white flex justify-between items-center">
                    <h1 className="text-2xl font-bold">EDIT VISITOR DETAILS</h1>
                    {!submitting ? (
                        <button
                            onClick={convertToMember}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400 transition-colors"
                            disabled={submitting}
                        >
                            Convert to Member
                        </button>
                    ):(
                        <button
                            
                            className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-800 focus:ring-2 focus:ring-offset-2 focus:ring-green-400 transition-colors"
                            disabled={submitting}
                        >
                            <RotatingLines
                                strokeColor="white"
                                strokeWidth="5"
                                animationDuration="0.75"
                                width="24"
                                height="24"
                            />
                            Converting...
                        </button>
                    )}
                </div>

                <div className="p-6">
                    {/* PERSONAL INFORMATION */}
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-800 border-b pb-3 mb-5">
                            PERSONAL INFORMATION
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Personal Info Details */}
                            <div className="md:col-span-2 space-y-5">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                    <InputField
                                        label="First Name"
                                        name="first_name"
                                        value={visitor.first_name}
                                        onChange={handleChange}
                                        required
                                        error={errors.first_name}
                                    />
                                    <InputField
                                        label="Last Name"
                                        name="last_name"
                                        value={visitor.last_name}
                                        onChange={handleChange}
                                        required
                                        error={errors.last_name}
                                    />
                                    <RadioGroup
                                        label="Gender"
                                        name="gender"
                                        options={[{ label: 'Male', value: 'Male' }, { label: 'Female', value: 'Female' }]}
                                        value={visitor.gender}
                                        onChange={handleChange}
                                        required
                                        error={errors.gender}
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                    <InputField
                                        label="Address"
                                        name="address"
                                        value={visitor.address}
                                        onChange={handleChange}
                                        required
                                        error={errors.address}
                                    />
                                    <InputField
                                        label="Phone Number"
                                        name="phone_number"
                                        value={visitor.phone_number}
                                        onChange={handleChange}
                                        required
                                        error={errors.phone_number}
                                    />
                                    <InputField
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={visitor.email}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                    <InputField
                                        label="Date of Birth"
                                        name="date_of_birth"
                                        type="date"
                                        value={visitor.date_of_birth}
                                        onChange={handleChange}
                                        required
                                        error={errors.date_of_birth}
                                    />
                                    <InputField
                                        label="Hometown"
                                        name="hometown"
                                        value={visitor.hometown}
                                        onChange={handleChange}
                                        required
                                        error={errors.hometown}
                                    />
                                    <InputField
                                        label="Nationality"
                                        name="nationality"
                                        value={visitor.nationality}
                                        onChange={handleChange}
                                        required
                                        error={errors.nationality}
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                    <InputField
                                        label="Residence"
                                        name="residence"
                                        value={visitor.residence}
                                        onChange={handleChange}
                                        required
                                        error={errors.residence}
                                    />
                                    <InputField
                                        label="Language Spoken"
                                        name="language_spoken"
                                        value={visitor.language_spoken}
                                        onChange={handleChange}
                                        required
                                        error={errors.language_spoken}
                                    />
                                    <div className="invisible"></div> {/* Empty space for layout */}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                    <RadioGroup
                                        label="Marital Status"
                                        name="marital_status"
                                        options={[
                                            { label: 'Single', value: 'Single' },
                                            { label: 'Married', value: 'Married' },
                                            { label: 'Divorced', value: 'Divorced' },
                                            { label: 'Remarried', value: 'Remarried' },
                                            { label: 'Co-Habitation', value: 'Co-Habitation' },
                                        ]}
                                        value={visitor.marital_status}
                                        onChange={handleChange}
                                        required
                                        error={errors.marital_status}
                                    />
                                </div>
                            </div>

                            {/* Image Upload */}
                            <div className="md:col-span-1 flex flex-col items-center justify-start py-5">
                                <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-blue-200 shadow-md mb-4">
                                    <img
                                        src={file ? URL.createObjectURL(file) : (visitor.profile_picture || UserImage)}
                                        alt="Visitor"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    name="profile_picture"
                                    id="profile_picture"
                                    onChange={handleChange}
                                    className="hidden"
                                />
                                <label
                                    htmlFor="profile_picture"
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md cursor-pointer transition-colors shadow-sm text-sm font-medium"
                                >
                                    <BsFillCloudUploadFill className="text-lg" />
                                    Upload File
                                </label>
                                <p className="mt-2 text-xs text-gray-500">
                                    {file ? file.name : (visitor.profile_picture ? "Image uploaded" : "No file selected")}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* SPIRITUAL LIFE INFORMATION */}
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-800 border-b pb-3 mb-5">
                            SPIRITUAL LIFE INFORMATION
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                            <InputField
                                label="Area of Ministry/Gifting"
                                name="ministry"
                                value={visitor.ministry}
                                onChange={handleChange}
                            />
                            <RadioGroup
                                label="Are You Born Again?"
                                name="born_again"
                                options={[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]}
                                value={visitor.born_again}
                                onChange={handleChange}
                                required
                                error={errors.born_again}
                            />
                            <RadioGroup
                                label="Holy Spirit Baptized?"
                                name="holy_spirit_baptized"
                                options={[{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }]}
                                value={visitor.holy_spirit_baptized}
                                onChange={handleChange}
                                required
                                error={errors.holy_spirit_baptized}
                            />
                        </div>
                    </div>

                    {/* Submit Button / Loader */}
                    <div className="flex justify-end pt-6 border-t border-gray-200">
                        {submitting ? (
                            <div className="flex items-center justify-center">
                                <ThreeDots
                                    height="40"
                                    width="40"
                                    radius="9"
                                    color="#2563EB"
                                    ariaLabel="three-dots-loading"
                                    visible={true}
                                />
                            </div>
                        ) : (
                            <button
                                onClick={submitForm}
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            >
                                Update Visitor
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VisitorDetail;